import { EmblaCarouselType } from 'embla-carousel';
import { ComponentPropsWithRef, useCallback, useEffect, useState } from 'react';
import {
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
} from 'react-icons/hi';

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

// custom hook to access prev/next button functionality
export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
): UsePrevNextButtonsType => {
  // integration of react hooks here
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  // function to handle prev button click
  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    if (onButtonClick) onButtonClick(emblaApi);
  }, [emblaApi, onButtonClick]);

  // function to handle next button click
  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    if (onButtonClick) onButtonClick(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type PropType = ComponentPropsWithRef<'button'>;

export const PrevButton: React.FC<PropType> = (props) => {
  // destructuring the props object here
  const { children, ...restProps } = props;

  // rendering prev button component here
  return (
    <button
      type='button'
      className='flex items-center justify-center bg-legal-brown size-[40px] rounded-tl-xl rounded-br-xl text-legal-dark-gray hover:opacity-70 active:scale-95 duration-300'
      {...restProps}
    >
      <HiOutlineChevronDoubleLeft className='size-5' />
      {children}
    </button>
  );
};

export const NextButton: React.FC<PropType> = (props) => {
  // destructuring the props object here
  const { children, ...restProps } = props;

  // rendering next button component here
  return (
    <button
      type='button'
      className='flex items-center justify-center bg-legal-brown size-[40px] rounded-tl-xl rounded-br-xl text-legal-dark-gray hover:opacity-70 active:scale-95 duration-300'
      {...restProps}
    >
      <HiOutlineChevronDoubleRight className='size-5' />
      {children}
    </button>
  );
};
